import useTimeoutActions from 'hooks/useTimeoutActions';
import './Home.scss';
import ScrollStack from '@components/scrollstack/ScrollStack';
import PhotoGallery from '@components/photogallery/PhotoGallery';
import Header from '@components/header/Header';
import Footer from '@components/footer/Footer';

const Home = () => {
    useTimeoutActions([
        { selector: '#hello-world', className: 'hide-caret', delay: 2000 },
        { selector: '#name', className: 'write', delay: 2200 },
        { selector: '#landing-image', className: 'fade-in', delay: 3000 },
        { selector: '.img-border', className: 'fade-in', delay: 3200 },
        { selector: '#name', className: 'hide-caret', delay: 4000 },
    ]);

    return (
        <>
            <a href="#main-content" className="skip-link">Skip to main content</a>
            <Header />
            <main id="main-content">
            <div id="hero">
                <div id="landing-background"></div>
                <div id="intro">
                    <div className="headings-container">
                        <div className="headings">
                            <span id="hello-world"></span>
                            <h1 id="name"></h1>
                        </div>
                        <div className="subheadings">
                            <h2 className="slide-in glass-btn">
                                <i className="bi bi-chevron-right" aria-hidden="true"></i> Full-Stack Software Developer
                            </h2>

                            <h2 className="slide-in">
                                <i className="bi bi-chevron-right" aria-hidden="true"></i> BSc Computer Science
                            </h2>

                            <h2 className="slide-in">
                                <i className="bi bi-chevron-right" aria-hidden="true"></i> Toronto, Canada
                            </h2>
                        </div>
                    </div>
                    <div className="img-wrapper">
                        <img id="landing-image" src="landing.png" alt="Chloe standing with her arms crossed" />
                    </div>
                </div>
            </div>

            <section id="about" className='section' aria-labelledby="about-heading">
                <div>
                    <h2 id="about-heading" className="dark-heading">About Me</h2>
                    <p>
                        Full-stack software engineer with 3+ years of experience developing high-scale
                        web applications with React, TypeScript, Next.js, Express, and PostgreSQL. Proven track
                        record of delivering enterprise-grade code for global clients at Publicis Groupe.
                        Experienced in building accessible design systems with
                        WCAG-compliant components. Passionate about using modern web technologies to 
                        advance health, nonprofit, and social-impact initiatives.
                    </p>
                </div>
                <div>
                    <h2 className="dark-heading">Skills</h2>
                    <ul>
                        <li>React</li>
                        <li>TypeScript</li>
                        <li>Next.js</li>
                        <li>Tailwind CSS</li>
                        <li>Contentful</li>
                        <li>WCAG 2.1 AA</li>
                        <li>HTML5, CSS3</li>
                        <li>Node.js (Express)</li>
                        <li>Python (FastAPI)</li>
                        <li>PostgreSQL</li>
                        <li>AWS</li>
                        <li>Microsoft Azure</li>
                        <li>PowerBI</li>
                        <li>Apache Airflow</li>
                    </ul>
                </div>
            </section>

            <section id="education" className='section' aria-labelledby="education-heading">
                <h2 id="education-heading" className="light-heading">Education</h2>
                <div className="item-header">
                    <h3>Bachelor of Science, McGill University</h3>
                    <span className='date'>2018 - 2022</span>
                </div>
                <h4>Major Computer Science, Minor Neuroscience</h4>
                <ul>
                    <li>Cumulative GPA of 3.95/4.00</li>
                    <li>Dean's Honour List with Distinction</li>
                    <li>Recipient of the Dr. Feng Qian Scholarship, awarded by the School of Computer Science to 3 students with outstanding academic achievement</li>
                    <li>Coursework in object-oriented design, algorithm design, data structures, complexity analysis, operating systems, machine learning, computer vision, multivariate calculus, probability, and statistics</li>
                </ul>
            </section>

            <section id="experience" className='section' aria-labelledby="experience-heading">
                <h2 id="experience-heading" className="dark-heading">Experience</h2>
                <div id="experiences">
                    <div className="experience-item">
                        <div className="item-header">
                            <h3>Full-Stack Software Engineer</h3>
                            <span className='date'>March - August 2024</span>
                        </div>
                        <h4>Epsilon (Publicis Groupe), Remote</h4>
                        <ul>
                            <li>Built and deployed a FastAPI microservice powering product-affinity scoring for a major retail client in North America, enabling personalized product suggestions across a host of e-commerce sites</li>
                            <li>Maintained automated ETL pipelines using Python and Apache Airflow
                                to process Adobe Analytics clickstream data, storing it in AWS S3 and
                                enabling SQL-based analysis via Amazon Athena</li>
                            <li>Developed data-driven Power BI dashboards to monitor scoring
                                accuracy, conversion performance, and business impact</li>
                        </ul>
                    </div>
                    <div className="experience-item">
                        <div className="item-header">
                            <h3>Frontend Software Engineer</h3>
                            <span className='date'>June 2022 - March 2024</span>
                        </div>
                        <h4>Publicis Sapient (Publicis Groupe), Toronto</h4>
                        <ul>
                            <li>Delivered accessible, high-performance UIs for major retail and automotive clients using React, Next.js, TypeScript, and Tailwind</li>
                            <li>Led accessibility-first development through the Accessibility Centre of Excellence, ensuring WCAG 2.1 AA compliance on all client work</li>
                            <li>Implemented key e-commerce features including product listings, reviews, checkout flows, and Global Payments integration</li>
                            <li>Collaborated with UX, PM, and QA in a fast-paced Agile environment to deliver iterative features and maintain automated testing pipelines on a frequent basis</li>
                        </ul>
                    </div>
                    <div className="experience-item">
                        <div className="item-header">
                            <h3>Fellow</h3>
                            <span className='date'>April - June 2021</span>
                        </div>
                        <h4>AI for Social Good Lab, Montréal</h4>
                        <ul>
                            <li>Participated in an intensive machine learning bootcamp covering mathematical fundamentals, convolutional and recurrent neural networks, and reinforcement learning</li>
                            <li>Orchestrated the development of a random-forest machine learning model that diagnoses a user with Parkinson's disease, ALS, or Cerebral Palsy from audio input</li>
                        </ul>
                    </div>
                    <div className="experience-item">
                        <div className="item-header">
                            <h3>Programmer / Research Intern</h3>
                            <span className='date'>May - August 2020</span>
                        </div>
                        <h4>Rotman Research Institute, Baycrest Health Sciences Centre, Toronto</h4>
                        <ul>
                            <li>Developed psychophysical experiments using JavaScript and PsychoJS to investigate how age influences the perception and memory of facial identity</li>
                            <li>Analyzed time-series magnetoencephalography (MEG) data and performed independent component analysis using MATLAB, EEGLAB, and SPM-12 packages to investigate how blink-related oscillations are influenced by Alzheimer's disease</li>
                            <li>Independently hosted a 2-hour workshop to teach my team how to use PsychoPy and PsychoJS libraries for experiment development</li>
                        </ul>
                    </div>
                    <div className="experience-item">
                        <div className="item-header">
                            <h3>
                                Research Intern
                            </h3>
                            <span className='date'>May - August 2019</span>
                        </div>
                        <h4>Toronto Centre for Liver Disease, University Health Network (UHN), Toronto</h4>
                        <ul>
                            <li>Optimized novel technology for rapid Hepatitis C Virus (HCV) diagnosis called 'DNA Amplification via Scissor-Like Structures' using clinical isolates and compared its sensitivity to existing commercial assays across all HCV genotypes</li>
                            <li>Conducted point-of-care testing for HCV on patients in the emergency room at Toronto Western Hospital and at homeless shelters across Toronto</li>
                        </ul>
                    </div>
                </div>
            </section>

            <section id="projects" className='section' aria-labelledby="projects-heading">
                <h2 id="projects-heading" className="light-heading">Projects</h2>
                <div className='project-item'>
                    <div className="item-header">
                        <h3>
                            Interviews by AI
                            <span style={{ margin: "0 10px" }}>~</span>
                            <a
                                href="https://interviewsby.ai"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="project-link"
                            >
                                interviewsby.ai
                            </a>
                        </h3>
                        <div className='item-header-right'>
                            <a href="https://interviewsby.ai" target="_blank" rel="noopener noreferrer" className="launch-btn" aria-label="Open Interviews by AI website in new tab">
                                Open <i className="bi bi-box-arrow-up-right" aria-hidden="true"></i>
                            </a>
                        </div>
                    </div>
                    <ul>
                        <li>
                            Built and launched an AI-powered SaaS platform (React, Next.js, TypeScript, PostgreSQL, Azure) serving <strong>3.5K MAU</strong>, <strong>150 DAU</strong>, and <strong>50K+ users</strong>
                        </li>
                        <li>
                            Grew revenue to <strong>$3K MRR</strong> with <strong>300 monthly paid subscribers</strong>
                        </li>
                        <li>
                            Achieved strong SEO performance, ranking <strong>#4 on Google</strong> for “AI interview”
                        </li>
                        <li>
                            Managed the full stack, implementing role-based access control and
                            integrating server-side services including GPT-4 and Whisper, Stripe for
                            payments, Google OAuth for authentication, and Azure for cloud deployment
                        </li>
                    </ul>

                </div>

                <ScrollStack cards={[
                    {
                        video: "https://cs2100320028857faae.blob.core.windows.net/interviewsbyai/step-3.mp4",
                        title: "Improve with AI coaching",
                        text: "Get instant AI feedback and an improved sample response showing how you could've answered the question."
                    },
                    {
                        video: "https://cs2100320028857faae.blob.core.windows.net/interviewsbyai/step-2.mp4",
                        title: "Practice answering",
                        text: "Record your answer with audio or text, simulating the interview experience."
                    },
                    {
                        video: "https://cs2100320028857faae.blob.core.windows.net/interviewsbyai/step-1.mp4",
                        title: "Generate questions",
                        text: "Paste a job description, receive realistic interview questions tailored to the role."
                    },
                ]} />

                <div className='project-item'>
                    <div className="item-header">
                        <h3>
                            StackScope
                            <span style={{ margin: "0 10px" }}>~</span>
                            <a
                                href="https://stackscope.xyz"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="project-link"
                            >
                                stackscope.xyz
                            </a>
                        </h3>
                        <div className='item-header-right'>
                            <a href="https://stackscope.xyz" target="_blank" rel="noopener noreferrer" className="launch-btn" aria-label="Open StackScope website in new tab">
                                Open <i className="bi bi-box-arrow-up-right" aria-hidden="true"></i>
                            </a>
                        </div>
                    </div>
                    <ul>
                        <li>
                            A modern web technology analyzer that detects frameworks, CMSs, analytics, auth providers, and HTTP headers
                        </li>
                        <li>
                            Implemented <strong>headless browser automation with Playwright</strong> to accurately analyze SPAs and fully hydrated HTML beyond static page analysis
                        </li>
                        <li>
                            Designed a <strong>serverless-first architecture</strong> using Browserless.io, enabling deployment on Vercel and other serverless platforms without managing browser binaries
                        </li>
                        <li>
                            Developed an <strong>accessible, responsive UI</strong> with React, TypeScript, Next.js, and Tailwind CSS, with full keyboard and screen-reader compatibility
                        </li>
                    </ul>
                </div>

                <ScrollStack cards={[
                    {
                        video: "https://cs2100320028857faae.blob.core.windows.net/portfolio/stackscope-2.mp4",
                        title: "Your detailed report",
                        text: "Detects frontend frameworks, content management systems, analytics tools, sign-in methods, and more."
                    },
                    {
                        video: "https://cs2100320028857faae.blob.core.windows.net/portfolio/stackscope-1.mp4",
                        title: "Analyze any website",
                        text: "Input a website URL to get a detailed report on its technology stack."
                    },
                ]} />

                <div className='project-item'>
                    <div className="item-header">
                        <h3>Elfenroads | Desktop Game with Java and Swing UI</h3>
                        <div className='item-header-right'>
                            <a href="https://github.com/cpappas18/elfenroads" target="_blank" rel="noopener noreferrer" className="launch-btn github-btn" aria-label="Open Elfenroads GitHub repository in new tab">
                                Open <i className="bi bi-github" aria-hidden="true"></i>
                            </a>
                        </div>
                    </div>
                    <ul>
                        <li>Designed and developed a <strong>turn-based, strategic board game</strong> for up to 6 players per session using <strong>Java</strong> and <strong>Swing UI</strong></li>
                        <li>Implemented a <strong>peer-to-peer network architecture</strong> and applied advanced software design patterns including <strong>Remote Command</strong> and <strong>Template Method</strong>, ensuring modularity, scalability, and maintainable code</li>
                        <li>Led development over 8 months as part of a 6-person team for an undergraduate capstone project, delivering a fully functional native desktop application with polished gameplay mechanics and a responsive user interface</li>
                    </ul>
                </div>

                <div className='project-item'>
                    <div className="item-header">
                        <h3>STARboard | Web App with LAMP Stack</h3>
                        <div className='item-header-right'>
                            <a href="https://github.com/cpappas18/STARboard" target="_blank" rel="noopener noreferrer" className="launch-btn github-btn" aria-label="Open STARboard GitHub repository in new tab">
                                Open <i className="bi bi-github" aria-hidden="true"></i>
                            </a>
                        </div>
                    </div>
                    <ul>
                        <li>Designed and developed a <strong>full-stack web application</strong> to manage teaching assistant (TA) responsibilities and records for the School of Computer Science at McGill University</li>
                        <li>Implemented role-based user accounts for <strong>students, professors, TAs, and administrators</strong>, ensuring secure access and streamlined workflow management</li>
                        <li>Won <strong>1st place</strong> in a competition for the best TA management website, outperforming 60 other teams</li>
                        <li>Built with <strong>JavaScript, PHP, HTML, CSS, Apache, and SQL</strong>, delivering a multi-page application with robust backend functionality and a user-friendly frontend interface</li>
                    </ul>
                </div>
            </section>

            <section id="travel" className='section' aria-labelledby="travel-heading">
                <h2 id="travel-heading" className="dark-heading">Travel</h2>
                <p>
                    Backpacking the world is one of my greatest passions! I've visited over 30 countries across Europe, Asia, and South America, immersing myself in diverse cultures and cuisines. Some of my favorite experiences include getting my Open Water scuba diving license in Thailand, biking the Ha Giang Loop in Vietnam, and sleeping under the Northern Lights in Iceland.
                </p>
                <PhotoGallery />
            </section>

            <Footer />
            </main>
        </>
    );
};

export default Home;